/**
 * Shared scope types for the Whakahaere config UI.
 *
 * ADR-126 attaches the operating calendar, routing policies, and terminology
 * overlay at either the org tier (district) or the site tier (school), with a
 * site override taking precedence over the inherited org default. The config
 * surfaces render a site-grouped table whose rows are scopes (an
 * "Organization default" row + one per site) rather than a dropdown selector.
 */

/** A resolved scope: org tier (school null) or site tier (school set). */
export interface ConfigScope {
  /** District (org) id — always set. */
  districtId: number;
  districtName: string;
  /** School (site) id when a site override; null for the org default. */
  schoolId: number | null;
  schoolName: string | null;
}

/** One row in a scope-grouped table: the org default or a specific site. */
export interface ScopeRow {
  /** Stable key: `org:<districtId>` or `site:<schoolId>`. */
  key: string;
  kind: 'org' | 'site';
  districtId: number;
  districtName: string;
  schoolId: number | null;
  schoolName: string | null;
  /** True when this scope has its own config (vs inheriting / unset). */
  hasOwn: boolean;
}

/** Project a table row back to the `ConfigScope` the drawers/forms consume. */
export function scopeOf(row: ScopeRow): ConfigScope {
  return {
    districtId: row.districtId,
    districtName: row.districtName,
    schoolId: row.schoolId,
    schoolName: row.schoolName,
  };
}

/** Scope key for an org-tier (district) attachment. */
export function orgKey(districtId: number): string {
  return `org:${districtId}`;
}

/** Scope key for a site-tier (school) attachment. */
export function siteKey(schoolId: number): string {
  return `site:${schoolId}`;
}

/**
 * Build the set of scope keys that have their OWN config, from a list of
 * objects each carrying `school` (site tier) and/or `district` (org tier).
 * Used to badge each table row Inherited vs Override/Configured.
 */
export function ownKeysFrom(
  items: ReadonlyArray<{ school?: number | null; district?: number | null }>,
): Set<string> {
  const keys = new Set<string>();
  for (const it of items) {
    if (it.school != null) keys.add(siteKey(it.school));
    else if (it.district != null) keys.add(orgKey(it.district));
  }
  return keys;
}
